import { InfiniteData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemoizedFn } from 'ahooks';
import { filter, flatten, map, uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import { productList, ProductListParams, ProductListResponse } from 'services/api/conversation';
import { ApiQueryKey } from 'services/config/queryKey';

type ProductV2QueryParams = Pick<
  ProductListParams,
  | 'internal_store_id'
  | 'keyword'
  | 'order_by'
  | 'internal_buyer_id'
  | 'keyword_type'
  | 'external_item_ids'
  | 'ttsToko'
>;

export const useProductV2 = ({
  internal_store_id,
  keyword,
  order_by,
  internal_buyer_id,
  keyword_type,
  external_item_ids,
  ttsToko,
}: ProductV2QueryParams) => {
  const queryClient = useQueryClient();

  const queryParams = useMemo(
    () => ({
      page_size: 5,
      internal_store_id,
      keyword,
      order_by,
      internal_buyer_id,
      keyword_type,
      needStockRule: true,
      external_item_ids,
      ttsToko,
    }),
    [
      internal_store_id,
      keyword,
      order_by,
      internal_buyer_id,
      keyword_type,
      external_item_ids,
      ttsToko,
    ],
  );
  const { isLoading, data, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [ApiQueryKey.ProductList, queryParams],
    queryFn: ({ queryKey, pageParam, signal }) => {
      const params = queryKey[1];
      return productList(
        {
          ...params,
          page_no: pageParam ?? 1,
        },
        signal,
      );
    },
    getNextPageParam: (lastPage) => {
      const { pageNo, hasNext } = lastPage.pageInfo;
      if (hasNext) {
        return pageNo + 1;
      }
    },
  });

  const productData = useMemo(() => {
    return filter(
      uniqBy(flatten(map(data?.pages, 'itemList')), 'internalItemId'),
      (item) => !!item,
    );
  }, [data]);

  const refresh = async () => {
    queryClient.resetQueries({
      queryKey: [ApiQueryKey.ProductList, queryParams],
      exact: true,
    });
    const { data } = await fetchNextPage({ pageParam: 1 });
    queryClient.setQueryData<InfiniteData<ProductListResponse>>(
      [ApiQueryKey.ProductList, queryParams],
      (oldData) => {
        if (data && oldData) {
          return {
            pages: [data.pages[0]], // 只保留第一页数据
            pageParams: [oldData.pageParams[0]], // 只保留第一页的 pageParam
          };
        }
        return oldData;
      },
    );
  };

  return {
    isLoading,
    productData,
    hasNextPage,
    fetchNextPage,
    refresh,
  };
};

interface UpdateProductParams {
  externalItemId: string;
  internal_store_id: string;
  internal_buyer_id: string;
}

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: ({ externalItemId, internal_store_id, internal_buyer_id }: UpdateProductParams) =>
      productList({
        page_no: 0,
        page_size: 1,
        keyword: externalItemId,
        keyword_type: 1,
        needStockRule: true,
        internal_store_id,
        internal_buyer_id,
      }),
    onSuccess: async ({ itemList }, { externalItemId, internal_store_id }: UpdateProductParams) => {
      const newProduct = itemList?.[0];
      if (!newProduct) return;

      // 更新商品列表的商品状态
      const queryKeys = queryClient.getQueryCache().findAll([ApiQueryKey.ProductList]);
      queryKeys.forEach(({ queryKey }) => {
        queryClient.setQueryData<InfiniteData<ProductListResponse>>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => {
              return {
                ...page,
                itemList: page.itemList?.map((cached) => {
                  if (cached.externalItemId === newProduct.externalItemId) {
                    return newProduct;
                  }
                  return cached;
                }),
              };
            }),
          };
        });
      });

      /**
       * 更新商品卡片库存状态
       */
      queryClient.setQueryData<Conversation.MessageProduct>(
        [
          ApiQueryKey.ItemCardProduct,
          {
            storeId: internal_store_id,
            externalItemId,
            needStockRule: true,
          },
        ],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            spuStockStatus: newProduct.spuStockStatus,
          };
        },
      );
    },
  });

  const updater = useMemoizedFn(
    (externalItemId: string, internal_store_id: string, internal_buyer_id: string) => {
      return mutateAsync({
        externalItemId,
        internal_store_id,
        internal_buyer_id,
      });
    },
  );

  return updater;
};
